import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Concatena classes do Tailwind resolvendo conflitos (a última vence). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Formata uma data no padrão brasileiro.
 *
 * Datas sem hora precisam de cuidado em fuso negativo. Duas formas chegam
 * aqui e ambas representam um dia civil, não um instante:
 *
 *   - `"1980-02-10"` — vindo de um `<input type="date">`;
 *   - `"1980-02-10T00:00:00.000Z"` — vindo de uma coluna `@db.Date` do Prisma.
 *
 * Interpretadas como instante UTC e renderizadas em UTC-3, as duas virariam
 * 09/02/1980 — um dia antes do que o usuário digitou. Por isso são formatadas
 * em UTC, enquanto valores com hora real seguem o fuso local.
 */
const APENAS_DATA = /^\d{4}-\d{2}-\d{2}$/;
const MEIA_NOITE_UTC = /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/;

export function formatarData(valor: string | Date | null | undefined): string {
  if (!valor) return '—';

  const ehDiaCivil =
    typeof valor === 'string' && (APENAS_DATA.test(valor) || MEIA_NOITE_UTC.test(valor));

  const data =
    typeof valor === 'string'
      ? new Date(APENAS_DATA.test(valor) ? `${valor}T00:00:00Z` : valor)
      : valor;

  if (Number.isNaN(data.getTime())) return '—';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    ...(ehDiaCivil ? { timeZone: 'UTC' } : {}),
  }).format(data);
}

/** Formata data e hora no padrão brasileiro. */
export function formatarDataHora(valor: string | Date | null | undefined): string {
  if (!valor) return '—';
  const data = typeof valor === 'string' ? new Date(valor) : valor;
  if (Number.isNaN(data.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(data);
}

/**
 * Converte para booleano o valor de um `<select>` de situação.
 *
 * Necessário porque o `setValueAs` do React Hook Form recebe ora a string do
 * DOM (`"true"`), ora o booleano que veio do estado do formulário. Comparar
 * direto com `'true'` faz o booleano `true` virar `false` — e um formulário
 * salvo sem tocar no campo desativava o registro silenciosamente.
 */
export function paraBooleano(valor: unknown): boolean {
  if (typeof valor === 'boolean') return valor;
  return valor === 'true';
}
